import localforage from 'localforage';
import { platformService } from '../services/platform.service';

const cacheSchema: LocalForageOptions = {
  driver: localforage.INDEXEDDB,
  name: 'tikit',
  storeName: 'users',
  version: 1
};

interface CachedUser extends PlatformUser {
  timeCached: number
}

const invalidationPeriod = 24 * 60 * 60 * 1000; // 1 day invalidation time
const cache: LocalForage = localforage.createInstance(cacheSchema);

let _api = null;
const platformAPI = (): platformService => {
  _api ??= new platformService();
  return _api;
};

// We need an dictionary for those calls that check before we return all the features, this way we can 'subscribe' to the cache getting set
const observer: { [id: number]: Array<(enabled) => void> } = {};
const pendingUser: { [id: number]: Promise<PlatformUser[]> } = {};

/**
 * In-memory mirror of the IndexedDB store.
 *
 * getCachedUsers sits in the critical path of every list refresh: the ticket response is resolved,
 * then loadUsers looks up each requester and assignee before setListStates is called, so no rows
 * render until this returns. It used to await one IndexedDB read per id, sequentially, which on
 * iOS Safari could stall until the next user interaction — the list stayed empty after switching
 * views until something else was tapped.
 *
 * A list page asks for the same handful of ids on every refresh, so after the first read this
 * serves entirely from memory and IndexedDB leaves the interactive path.
 */
const memory = new Map<number, CachedUser>();

const isFresh = (user: CachedUser | null | undefined): user is CachedUser =>
  !!user && (Date.now() - user.timeCached) <= invalidationPeriod;

const remember = (id: number, user: CachedUser) => {
  memory.set(id, user);
};

export const clearUserCache = async () => {
  memory.clear();
  await cache.clear();
};

const getUsers = (ids: number[]): Promise<PlatformUser[]> => {
  const c = platformAPI().getBulkUsersById(ids);
  ids.forEach(id => {
    if (!pendingUser[id])
      pendingUser[id] = c;

    pendingUser[id]
      .then(r => {
        const u = r.find(x => x.Id == id);
        // The bulk API can omit an id from its response (deleted user, replication lag, ...)
        // without rejecting. Caching a { timeCached } entry with no real user data in that case
        // would satisfy isFresh() and get served back as a valid user for a full invalidation period.
        if (!u) return;

        const entry: CachedUser = { ...u, timeCached: Date.now() };
        remember(id, entry);
        return cache.setItem<CachedUser>(id.toString(), entry);
      })
      // A rejected bulk fetch, or a failed cache write, must not leave pendingUser[id] wedged on a
      // settled promise forever - finally() below always clears it so the next call retries instead
      // of silently reusing a promise that will never resolve to anything useful again.
      .catch(() => {})
      .finally(() => { delete pendingUser[id]; });
  });

  return c;
}

export const getCachedUsers = async (ids: number[]): Promise<PlatformUser[]> => {
  ids = [...new Set(ids)].filter(x => x > 0);

  const users: PlatformUser[] = [];
  const unknown: number[] = [];

  for (const id of ids) {
    const remembered = memory.get(id);
    if (isFresh(remembered)) users.push(remembered);
    else unknown.push(id);
  }

  if (unknown.length > 0) {
    // One batch rather than a sequential await per id: N round trips became one, and a storage
    // failure degrades to a network fetch instead of rejecting the whole list refresh.
    const stored = await Promise.all(
      unknown.map(id => cache.getItem<CachedUser>(id.toString()).catch(() => null))
    );

    const fetch: number[] = [];
    unknown.forEach((id, index) => {
      const user = stored[index];
      if (isFresh(user)) {
        remember(id, user);
        users.push(user);
      } else {
        fetch.push(id);
      }
    });

    if (fetch.length > 0) {
      const response = await getUsers(fetch);
      users.push(...response);
    }
  }

  return users;
}

export const getCachedUser = async (id: number, forceUpdate?: boolean): Promise<PlatformUser> => {
  if (!id || id < 0) return undefined;

  forceUpdate ??= false;
  let user = forceUpdate ? undefined : await cache.getItem<CachedUser>(id.toString());

  if (user === undefined || user === null || (Date.now() - user.timeCached) > invalidationPeriod) {
    const rsp = await platformAPI().getUser(id.toString())
    if (rsp?.data) {
      cache.setItem<CachedUser>(id.toString(), { ...rsp.data, timeCached: Date.now() });
      user = rsp.data;
    }
  }

  return user;
}

export const syncCachedUser = async (user: PlatformUser): Promise<CachedUser> => cache.setItem<CachedUser>(user.Id.toString(), { ...user, timeCached: Date.now() });

export const searchUsers = async (user: PlatformUser, create?: boolean): Promise<PlatformUser> => {
  create ??= true;
  let result = await cache.iterate<PlatformUser, PlatformUser>(x => { if ((x.AadObjectId && x.AadObjectId == user.AadObjectId) || x.Id == user.Id || x.UserName == user.UserName) return x; });

  if (!result) {
    result = await (create ? platformAPI().getOrCreatePlatformUser(user) : platformAPI().searchPlatformUser(user));
    if (result) 
      cache.setItem<CachedUser>(result.Id.toString(), { ...result, timeCached: Date.now() });
  }

  return result;
}